import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IonButton, IonButtons, IonCheckbox, IonContent, IonHeader, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonItem, IonLabel, IonList, IonModal, IonRadio, IonRadioGroup, IonSelect, IonSelectOption, IonSkeletonText, IonTextarea, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { DateRange, DateUtil } from "@qid/core";
import { SuperButtonComponent } from "@qid/super";
import { ApiNames } from "../../../../enum/api-names.enum";
import { ApiCreditLogService } from "../../../../services/api-credit-log.service";
import { ApiCreditService } from '../../../api-credit';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { activeAccountChanges } from '../../../authentication';
import { PlanType } from "../../../plan/lib/enum/plan-type.enum";
import { PlanPurchase } from '../../models/plan-purchase.model';
import { Order } from '../../../order/interface/order.interface';
import { OrderApi } from '../../../order/services/order.api';
import { AutoRenewMandate, isCancellableAutoRenewMandate } from '../../../order/interface/auto-renew-mandate.interface';
import { OrderStatus } from '../../../order/enum/order-status.enum';
import { PlanPurchaseService } from '../../services/plan-purchase.service';
import { UpgradePlanPage } from 'libs/common/src/pages/upgrade-plan/upgrade-plan.page';
import { ENVIRONMENT, Environment } from '@qid/env';
import { DisplayService } from '@qid/core';

const CANCEL_REASON_OTHER = 'Other';

@Component({
  selector: 'app-view-plan-purchase',
  templateUrl: './view-plan-purchase.page.html',
  styleUrls: ['./view-plan-purchase.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SuperButtonComponent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonSkeletonText,
    IonItem,
    IonLabel,
    IonList,
    IonSelect,
    IonSelectOption,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonIcon,
    IonButton,
    IonButtons,
    IonModal,
    IonRadio,
    IonRadioGroup,
    IonTextarea
  ]
})
export class ViewPlanPurchasePage implements OnInit {
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;

  public primaryActivePurchases: PlanPurchase[];
  public activeAddOns: PlanPurchase[];
  public expiredAddOns: PlanPurchase[]
  public isActivePlan: boolean
  public historyDocs: Order[] = [];
  public historyLoaded: boolean = false;
  private historyPage: number = 1;
  private historyHasNext: boolean = false;
  public downloadingInvoiceId: string | null = null;
  public apiBalance: number = 0
  public selectedCostRange: DateRange = DateRange.LAST_30_D
  public createDocumentApiCost = 0
  public readDocumentApiCost = 0
  public isShowApiCostLogs: boolean = false;
  public rangeOptions: string[] = Object.values(DateRange);
  public isLoadingCosts: boolean = false;
  public isManageModalOpen = false;
  public manageModalStep: 'options' | 'cancel-reason' | 'remove-reason' = 'options';
  public managingPlanPurchase: PlanPurchase | null = null;
  public selectedCancelReason: string | null = null;
  public otherCancelReason = '';
  public cancelConsentAccepted = false;
  public removeRemark = '';
  public removeConsentAccepted = false;
  public isCancelling = false;
  public isRemoving = false;
  public autoRenewMandates: AutoRenewMandate[] = [];
  public readonly cancelReasons = [
    'Too expensive',
    'Missing features I need',
    'Switching to another tool',
    'Temporary pause / not needed right now',
    CANCEL_REASON_OTHER,
  ];
  public readonly cancelReasonOther = CANCEL_REASON_OTHER;
  private leave$ = new Subject<void>();

  constructor(
    @Inject(ENVIRONMENT) private environment: Environment,
    private planPurchaseService: PlanPurchaseService,
    private orderApi: OrderApi,
    private apiCreditService: ApiCreditService,
    private apiCreditLogService: ApiCreditLogService,
    private router: Router,
    private store: Store,
    private modalController: ModalController,
    private displayService: DisplayService,
  ) {
  }

  ngOnInit() {

  }

  ionViewDidEnter() {
    this.leave$ = new Subject<void>();
    activeAccountChanges(this.store)
      .pipe(takeUntil(this.leave$))
      .subscribe(() => {
        this.getPlan();
        this.fetchHistory();
        this.fetchBalance();
      });
  }

  ionViewDidLeave() {
    this.leave$.next();
    this.leave$.complete();
  }

  async getPlan() {
    const activePlans = await this.planPurchaseService.readActivePlans();
    try {
      this.autoRenewMandates = await this.orderApi.readAutoRenew() || [];
    } catch {
      this.autoRenewMandates = [];
    }
    this.filterPurchases(activePlans || []);
  }

  canCancelAutoRenew(planPurchase: PlanPurchase): boolean {
    if (!planPurchase?.razorpaySubscriptionId || planPurchase.autoRenews === false) {
      return false;
    }

    const mandate = this.autoRenewMandates.find(
      (entry) => entry.razorpaySubscriptionId === planPurchase.razorpaySubscriptionId,
    );
    if (mandate) {
      return isCancellableAutoRenewMandate(mandate);
    }

    // Fallback when auto-renew list is empty/unavailable — trust active plan purchase.
    return true;
  }

  showAutoRenewBadge(planPurchase: PlanPurchase): boolean {
    return planPurchase.autoRenews && !!planPurchase.razorpaySubscriptionId;
  }

  isActivePlanPurchase(planPurchase: PlanPurchase): boolean {
    return !planPurchase.isExpired && new Date(planPurchase.expireTime).getTime() > Date.now();
  }

  canChangePaymentMethod(planPurchase: PlanPurchase): boolean {
    return !!planPurchase?.razorpaySubscriptionId && planPurchase.autoRenews;
  }

  canRemovePlan(planPurchase: PlanPurchase): boolean {
    return !!planPurchase?._id && this.isActivePlanPurchase(planPurchase) && !planPurchase.autoRenews;
  }

  showManagePlan(planPurchase: PlanPurchase): boolean {
    return this.canChangePaymentMethod(planPurchase)
      || this.canCancelAutoRenew(planPurchase)
      || this.canRemovePlan(planPurchase);
  }

  async fetchHistory() {
    const history = await this.planPurchaseService.readBillingHistory(1);
    this.historyDocs = history.docs || [];
    this.historyPage = history.page || 1;
    this.historyHasNext = !!history.hasNext;
    this.historyLoaded = true;
  }

  async infiniteLoad($event: any) {
    if (!this.historyHasNext) {
      this.infiniteScroll?.complete();
      return;
    }
    const nextPage = await this.planPurchaseService.readBillingHistory(this.historyPage + 1);
    this.historyDocs = this.historyDocs.concat(nextPage.docs || []);
    this.historyPage = nextPage.page || this.historyPage + 1;
    this.historyHasNext = !!nextPage.hasNext;
    this.infiniteScroll?.complete();
  }

  /** Amount inclusive of 18% GST from the stored order breakup (falls back to the persisted invoice total). */
  getOrderAmount(order: Order): number | null {
    if (order.priceBreakup?.payableAmount != null) {
      return Math.round(order.priceBreakup.payableAmount * 100) / 100;
    }
    if (order.invoiceTotal != null) {
      return Math.round(order.invoiceTotal * 100) / 100;
    }
    return null;
  }

  /** Billing-history status reflects the order/payment status (orders carry no expiry). */
  getOrderStatusLabel(order: Order): string {
    switch (order.orderStatus) {
      case OrderStatus.SUBSCRIPTION_OPEN:
      case OrderStatus.PAID:
        return 'Paid';
      case OrderStatus.CANCELLED:
        return 'Cancelled';
      default:
        return 'Pending';
    }
  }

  isOrderPaid(order: Order): boolean {
    return order.orderStatus === OrderStatus.SUBSCRIPTION_OPEN || order.orderStatus === OrderStatus.PAID;
  }

  async downloadInvoice(order: Order) {
    if (!order?.invoiceNumber || this.downloadingInvoiceId) {
      return;
    }
    try {
      this.downloadingInvoiceId = order._id;
      await this.planPurchaseService.downloadInvoice(order._id, order.invoiceNumber);
    } finally {
      this.downloadingInvoiceId = null;
    }
  }

  private markExpired(purchases: PlanPurchase[]) {
    const todayTime = new Date().getTime();
    purchases.forEach((purchase) => {
      if (new Date(purchase.expireTime).getTime() <= todayTime) {
        purchase.isExpired = true;
      }
    });
  }

  filterPurchases(activePlans: PlanPurchase[]) {
    this.markExpired(activePlans);
    this.isActivePlan = activePlans.length > 0
    this.primaryActivePurchases = activePlans.filter(purchase => purchase.plan?.type == PlanType.primary && !purchase.isExpired)
    this.activeAddOns = activePlans.filter(purchase => purchase.plan?.type == PlanType.addon && !purchase.isExpired)
    this.expiredAddOns = activePlans.filter(purchase => purchase.plan?.type == PlanType.addon && purchase.isExpired)
  }

  openBranchBilling = () => {
    this.router.navigateByUrl('/menu/settings/branch-billing');
  };

  openPlanUpgrade = async () => {

    const modal = await this.modalController.create({
      component: UpgradePlanPage,
      cssClass: 'fullscreen-modal',
    })
    await modal.present();
    const res = await modal.onDidDismiss();
    if (res?.data?.success) {
      this.router.navigate(['/menu/settings/view-plan'])
    }
  }


  fetchBalance = async () => {

    const res = await this.apiCreditService.fetchBalance();
    if (res?.data?.balance) {
      this.apiBalance = res.data.balance;
    }
  }


  async fetchApiCosts() {
    try {
      this.isLoadingCosts = true
      await this.fetchReadDocumentCost();
      await this.fetchCreateDocumentCost();
    } catch (e) {

    } finally {
      this.isLoadingCosts = false;
    }

  }

  async fetchCreateDocumentCost() {
    const range = DateUtil.getDateRange(this.selectedCostRange)
    const res = await this.apiCreditLogService.getCost(ApiNames.CreateDocumentAccess, range.startDate, range.endDate)
    if (res.data) {
      this.createDocumentApiCost = +res.data
    }
  }

  async fetchReadDocumentCost() {
    const range = DateUtil.getDateRange(this.selectedCostRange)
    const res = await this.apiCreditLogService.getCost(ApiNames.ReadDocumentAccess, range.startDate, range.endDate)
    if (res.data) {
      this.readDocumentApiCost = +res.data
    }
  }

  onManagePlanClicked = (planPurchase: PlanPurchase) => () => {
    this.managingPlanPurchase = planPurchase;
    this.manageModalStep = 'options';
    this.selectedCancelReason = null;
    this.otherCancelReason = '';
    this.cancelConsentAccepted = false;
    this.removeRemark = '';
    this.removeConsentAccepted = false;
    this.isManageModalOpen = true;
  };

  dismissManageModal() {
    this.isManageModalOpen = false;
    this.manageModalStep = 'options';
    this.managingPlanPurchase = null;
    this.selectedCancelReason = null;
    this.otherCancelReason = '';
    this.cancelConsentAccepted = false;
    this.removeRemark = '';
    this.removeConsentAccepted = false;
  }

  onManageBackClicked() {
    if (this.manageModalStep === 'cancel-reason' || this.manageModalStep === 'remove-reason') {
      this.manageModalStep = 'options';
      this.selectedCancelReason = null;
      this.otherCancelReason = '';
      this.cancelConsentAccepted = false;
      this.removeRemark = '';
      this.removeConsentAccepted = false;
      return;
    }
    this.dismissManageModal();
  }

  onPaymentMethodClicked() {
    const planPurchase = this.managingPlanPurchase;
    this.dismissManageModal();

    if (!planPurchase?.razorpaySubscriptionId) {
      return;
    }

    const mode = this.environment.production ? 'l' : 't';
    window.open(
      `https://api.razorpay.com/v1/${mode}/subscriptions/${planPurchase.razorpaySubscriptionId}`,
      '_blank'
    );
  }

  onCancelSubscriptionClicked() {
    if (!this.managingPlanPurchase?._id) {
      return;
    }

    this.selectedCancelReason = null;
    this.otherCancelReason = '';
    this.cancelConsentAccepted = false;
    this.manageModalStep = 'cancel-reason';
  }

  onRemovePlanClicked() {
    if (!this.canRemovePlan(this.managingPlanPurchase)) {
      return;
    }

    this.removeRemark = '';
    this.removeConsentAccepted = false;
    this.manageModalStep = 'remove-reason';
  }

  get canConfirmRemove(): boolean {
    return !!this.removeRemark.trim() && !this.isRemoving;
  }

  confirmRemovePlan = async () => {
    const planPurchase = this.managingPlanPurchase;
    const remark = this.removeRemark.trim();

    if (!planPurchase?._id || !remark || !this.canRemovePlan(planPurchase)) {
      return;
    }

    try {
      this.isRemoving = true;
      await this.planPurchaseService.removePlan(planPurchase._id, remark);
      await this.getPlan();
      this.dismissManageModal();
      await this.displayService.toast({
        message: 'Plan removed. Your organisation is now on the free tier.',
        duration: 4000,
        color: 'success',
      });
    } catch {
      await this.displayService.toast({ message: 'Could not remove plan', color: 'danger' });
    } finally {
      this.isRemoving = false;
    }
  };

  get cancelRemark(): string {
    if (this.selectedCancelReason === CANCEL_REASON_OTHER) {
      return this.otherCancelReason.trim();
    }
    return this.selectedCancelReason?.trim() || '';
  }

  get canConfirmCancel(): boolean {
    return !!this.cancelRemark && this.cancelConsentAccepted && !this.isCancelling;
  }

  confirmCancelSubscription = async () => {
    const planPurchase = this.managingPlanPurchase;
    const razorpaySubscriptionId = planPurchase?.razorpaySubscriptionId;
    const remark = this.cancelRemark;

    if (!razorpaySubscriptionId || !remark || !this.canCancelAutoRenew(planPurchase)) {
      return;
    }

    const expireTime = planPurchase.expireTime;

    try {
      this.isCancelling = true;
      await this.orderApi.cancelAutoRenew(razorpaySubscriptionId);
      await this.getPlan();
      this.dismissManageModal();
      await this.displayService.toast({
        message: `Auto-renewal cancelled. Your plan remains active until ${new Date(expireTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.`,
        duration: 4000,
        color: 'success',
      });
    } finally {
      this.isCancelling = false;
    }
  };

  onViewApiCostsClicked() {
    this.isShowApiCostLogs = true;
    this.fetchApiCosts();
  }

  protected readonly DateRange = DateRange;
}
